import { useEffect } from "react";

import { isTextInputTarget } from "../lib/domGuards";
import { type Citation } from "../models/audit/citation";

interface EvidenceItem {
  source: Citation;
  screenshot: Citation | null;
}

/**
 * Arrow-key navigation over a list of distinct evidence items (source/screenshot
 * pairs collapsed). Up/Left → previous, Down/Right → next; one press = one item,
 * independent of the underlying citation ordering. Selecting prefers the
 * screenshot view. Shared by the Workpaper Review trail and the Agent Steps pane
 * so the two stay in lock-step; pass `enabled: false` to stand down (e.g. when
 * the other pane owns the arrows).
 */
export function useEvidenceArrowNav({
  items,
  activeCitationId,
  enabled = true,
  onSelect,
}: {
  items: EvidenceItem[];
  activeCitationId?: string;
  enabled?: boolean;
  onSelect: (citation: Citation) => void;
}) {
  useEffect(() => {
    if (!enabled || items.length === 0) return;
    const onKeyDown = (e: KeyboardEvent) => {
      const prev = e.key === "ArrowUp" || e.key === "ArrowLeft";
      const next = e.key === "ArrowDown" || e.key === "ArrowRight";
      if ((!prev && !next) || isTextInputTarget(e.target)) return;
      e.preventDefault();
      const current = items.findIndex(
        (it) =>
          it.source.id === activeCitationId ||
          it.screenshot?.id === activeCitationId,
      );
      const nextIndex =
        current < 0
          ? 0
          : next
            ? Math.min(current + 1, items.length - 1)
            : Math.max(current - 1, 0);
      const item = items[nextIndex];
      if (item) onSelect(item.screenshot ?? item.source);
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [items, activeCitationId, enabled, onSelect]);
}
